import { SSE } from 'sse.js';

export const API_URL = process.env.REACT_APP_API_URL
export const NOTIFICATION_SERVER_URL = process.env.REACT_APP_NOTIFICATION_SERVER_URL

export const fetchWithHandling = async (url, options) => {
  try {
    options.credentials = "include";
    const response = await fetch(url, options);

    if (response.status === 401) {
      // Handle token expiry: Clear local storage and redirect
      localStorage.clear();
      window.location.href = '/login'; // Update to match your app's login page
      return;
    }

    console.log("Response Status:", response.status)

    if (!response.ok) {
      const errorDetails = await response.json().catch(() => ({}));
      console.error("API ERROR DETAILS:", errorDetails)
      throw (errorDetails.error || errorDetails.detail || errorDetails.message || `Error: ${response.status} ${response.statusText}`);
    }
    if (response.status === 204) {
      return;  // No body to parse
    }

    return response.json();
  } catch (error) {
    console.error('API Error:', error);
    throw error; // Propagate the error for custom handling in specific functions
  }
};

/**
 * Checks if the current user has admin privileges.
 * @param {string} accessToken - The user's JWT or session token.
 * @returns {Promise<object>} - A promise that resolves with the admin status.
 */
export const checkAdminStatus = async (accessToken) => {
  return fetchWithHandling(`${API_URL}/admin/checkAdmin`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
  });
};

/**
 * Fetches a paginated, filtered, and sorted list of all users.
 * @param {string} accessToken - The admin's JWT or session token.
 * @param {object} params - Object containing page, limit, search, sortConfig, and enterpriseOnly.
 * @returns {Promise<object>} - A promise that resolves with the paginated user data.
 */
export const fetchAllUsers = async (accessToken, params = {}) => {
  const { page = 1, limit = 10, search = '', sortConfig = [], enterpriseOnly = false } = params;

  const url = new URL(`${API_URL}/admin/users`);
  url.searchParams.append('page', page);
  url.searchParams.append('limit', limit);

  // NEW: Format the sortConfig array into a string
  if (sortConfig.length > 0) {
    const sortString = sortConfig
      .map(sort => (sort.direction === 'desc' ? `-${sort.key}` : sort.key))
      .join(',');
    url.searchParams.append('sort', sortString);
  }

  if (search) {
    url.searchParams.append('search', search);
  }
  if (enterpriseOnly) {
    url.searchParams.append('enterprise_only', String(enterpriseOnly));
  }

  return fetchWithHandling(url.toString(), {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
  });
};

/**
 * Triggers a download of the users list as an Excel file.
 * @param {string} accessToken - The admin's JWT or session token.
 */
export const exportUsersToExcel = async (accessToken) => {
  try {
    const response = await fetch(`${API_URL}/admin/users/export`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      },
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error(`Export failed: ${response.statusText}`);
    }

    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = "users_export.xlsx";
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);

  } catch (error) {
    console.error('Export Error:', error);
    throw error; // Propagate for handling in the component
  }
};

/**
 * Fetches the access level configuration from the backend.
 * @param {string} accessToken - The user's JWT or session token.
 * @returns {Promise<object>} - A promise that resolves with the access level configuration object.
 */
export const fetchAccessLevels = async (accessToken) => {
  return fetchWithHandling(`${API_URL}/admin/access-levels`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
  });
};

/**
 * Updates the access level for a specific user.
 * @param {string} accessToken - The admin's JWT or session token.
 * @param {string} userId - The ID of the user to update.
 * @param {string} accessLevel - The new access level ('basic', 'free', 'paid', 'premium').
 * @returns {Promise<object>} - A promise that resolves with the success message and updated user data.
 */
export const updateUserAccessLevel = async (accessToken, userId, accessLevel) => {
  return fetchWithHandling(`${API_URL}/admin/users/${userId}/access-level`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ access_level: accessLevel }),
  });
};

/**
 * Fetches the list of pre-approved users.
 * @param {string} accessToken - The admin's JWT.
 * @param {object} params - Pagination parameters { page, limit }.
 * @returns {Promise<object>} - Paginated list of pre-approved users.
 */
export const fetchNewUserAccessList = async (accessToken, params = {}) => {
  const { page = 1, limit = 10 } = params;
  const url = new URL(`${API_URL}/admin/new-user-access`);
  url.searchParams.append('page', page);
  url.searchParams.append('limit', limit);

  return fetchWithHandling(url.toString(), {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
  });
};

/**
 * Adds a new user to the pre-approved access list.
 * @param {string} accessToken - The admin's JWT.
 * @param {object} data - The user data { email, access_level }.
 * @returns {Promise<object>} - The success response.
 */
export const addNewUserAccess = async (accessToken, data) => {
  return fetchWithHandling(`${API_URL}/admin/new-user-access`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
    body: JSON.stringify(data),
  });
};

/**
 * Deletes a user from the pre-approved access list.
 * @param {string} accessToken - The admin's JWT.
 * @param {string} email - The email of the user to delete.
 * @returns {Promise<void>}
 */
export const deleteNewUserAccess = async (accessToken, email) => {
  // Note: fetchWithHandling is designed to handle JSON responses. 
  // A 204 No Content response has no body, so we handle it specially.

  // use fetchWithHandling for consistent error handling
  const url = `${API_URL}/admin/new-user-access/${encodeURIComponent(email)}`;
  return fetchWithHandling(url, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
    },
  });
};

/**
 * Fetches the accurate task token usage for a specific user.
 * @param {string} accessToken - The admin's JWT.
 * @param {string} email - The email of the user to query.
 * @returns {Promise<object>} - A promise that resolves with the task token data.
 */
export const fetchTaskTokenUsage = async (accessToken, email) => {
  const url = new URL(`${API_URL}/admin/user/chat-tokens`);
  url.searchParams.append('email', email);

  return fetchWithHandling(url.toString(), {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
  });
};

/**
 * Fetches the accurate workflow token usage for a specific user.
 * @param {string} accessToken - The admin's JWT.
 * @param {string} email - The email of the user to query.
 * @returns {Promise<object>} - A promise that resolves with the workflow token data.
 */
export const fetchWorkflowTokenUsage = async (accessToken, email) => {
  const url = new URL(`${API_URL}/admin/user/workflow-chat-tokens`);
  url.searchParams.append('email', email);

  return fetchWithHandling(url.toString(), {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
  });
};

// --- Access Control (Roles & Permissions) API Functions ---

export const fetchRoles = async (accessToken, projectId = null, isActive = true) => {
  const url = new URL(`${API_URL}/api/access-control/roles`);
  if (projectId) url.searchParams.append('project_id', projectId);
  url.searchParams.append('is_active', isActive);

  return fetchWithHandling(url.toString(), {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
    },
  });
};

export const createRole = async (accessToken, roleData) => {
  return fetchWithHandling(`${API_URL}/api/access-control/roles`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
    body: JSON.stringify(roleData),
  });
};

export const updateRole = async (accessToken, roleId, roleData) => {
  return fetchWithHandling(`${API_URL}/api/access-control/roles/${roleId}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
    body: JSON.stringify(roleData),
  });
};

export const deleteRole = async (accessToken, roleId) => {
  return fetchWithHandling(`${API_URL}/api/access-control/roles/${roleId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
    },
  });
};

export const assignRoleToUser = async (accessToken, userId, roleId, expiresAt = null) => {
  return fetchWithHandling(`${API_URL}/api/access-control/users/${userId}/roles`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ role_id: roleId, expires_at: expiresAt }),
  });
};

export const revokeRoleFromUser = async (accessToken, userId, roleId) => {
  return fetchWithHandling(`${API_URL}/api/access-control/users/${userId}/roles/${roleId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
    },
  });
};

export const getUserRoles = async (accessToken, userId) => {
  return fetchWithHandling(`${API_URL}/api/access-control/users/${userId}/roles`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
    },
  });
};

export const getUsersByRole = async (accessToken, roleId) => {
  return fetchWithHandling(`${API_URL}/api/access-control/roles/${roleId}/users`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
    },
  });
};


// --- MCP Admin API Functions (Refactored) ---

/**
 * Fetches all MCP connection templates for the admin dashboard.
 * @param {string} accessToken - The admin's JWT.
 * @returns {Promise<Array<object>>} A promise that resolves with the list of MCP origins.
 */
export const fetchAllMcpOrigins = (accessToken) => {
  return fetchWithHandling(`${API_URL}/admin/mcp/origins`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
    },
  });
};

/**
 * Creates a new MCP connection template.
 * @param {string} accessToken - The admin's JWT.
 * @param {object} data - The data for the new MCP origin.
 * @returns {Promise<object>} A promise that resolves with the success message.
 */
export const createMcpOrigin = (accessToken, data) => {
  return fetchWithHandling(`${API_URL}/admin/mcp/origins`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
    body: JSON.stringify(data),
  });
};

/**
 * Updates an existing MCP connection template.
 * @param {string} accessToken - The admin's JWT.
 * @param {string} id - The ID of the MCP origin to update.
 * @param {object} data - The update data.
 * @returns {Promise<object>} A promise that resolves with the success message.
 */
export const updateMcpOrigin = (accessToken, id, data) => {
  return fetchWithHandling(`${API_URL}/admin/mcp/origins/${id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
    body: JSON.stringify(data),
  });
};

/**
 * Deletes an MCP connection template.
 * @param {string} accessToken - The admin's JWT.
 * @param {string} id - The ID of the MCP origin to delete.
 * @returns {Promise<void>} A promise that resolves on successful deletion.
 */
export const deleteMcpOrigin = (accessToken, id) => {
  return fetchWithHandling(`${API_URL}/admin/mcp/origins/${id}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
    },
  });
};

export const chat = async (data, accessToken) => {

  return fetchWithHandling(`${API_URL}/chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`, // Include the token here
    },
    body: JSON.stringify(data),
  });
};

export const callFunction = async (data, accessToken) => {
  return fetchWithHandling(`${API_URL}/callBotFunction`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`, // Include the token here
    },
    body: JSON.stringify(data),
  });
};

export const createTask = async (data, accessToken) => {
  return fetchWithHandling(`${API_URL}/createTask`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`, // Include the token here
    },
    body: JSON.stringify(data),
  });
};

export const authApp = async (appname, accessToken) => {
  return fetchWithHandling(`${API_URL}/authApp/${appname}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`, // Include the token here
    },
    body: JSON.stringify({

      // is_bot: !!bot_id,  // Using double negation to ensure it's true/false
      // bot_id: bot_id || null  // Ensuring `bot_id` is sent as null if falsy
    }),
  });
};

export const unauthApp = async (appname, bot_id, accessToken) => {
  return fetchWithHandling(`${API_URL}/unauthorize_app/${appname}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`, // Include the token here
    },
    body: JSON.stringify({

      is_bot: !!bot_id,  // Using double negation to ensure it's true/false
      bot_id: bot_id || null  // Ensuring `bot_id` is sent as null if falsy
    }),
  });

};

export const getAuthenticatedApps = async (accessToken, bot_id = null) => {
  const is_bot = !!bot_id;  // Check if bot_id exists and set is_bot to true/false

  return fetchWithHandling(`${API_URL}/get_authenticated_apps`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`, // Include the token here
    },
    body: JSON.stringify({
      bot_id: bot_id || null,          // Send bot_id if provided, otherwise null
      is_bot: is_bot                   // Send is_bot as true if bot_id exists
    }),
  });

};

export const getToolsForSubtask = async (data, accessToken) => {
  return fetchWithHandling(`${API_URL}/getToolsForSubtask`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`, // Include the token here
    },
    body: JSON.stringify(data),
  });

}

export const fetchTasks = async (accessToken) => {
  // const response = await fetch(`${API_URL}/tasks?user_google_id=${userGoogleId}`);
  return fetchWithHandling(`${API_URL}/tasks`, { headers: { 'Authorization': `Bearer ${accessToken}` } });

};

export const fetchTaskConversations = async (taskId, accessToken = "") => {
  return fetchWithHandling(`${API_URL}/tasks/${taskId}/chats`, { headers: { 'Authorization': `Bearer ${accessToken}` } });
};

export const addConversationToTask = async (taskId, conversation, accessToken) => {
  return fetchWithHandling(`${API_URL}/task/${taskId}/conversation`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`
    },
    body: JSON.stringify({ conversation }),
  });
};

// File upload function
export const uploadFiles = async (accessToken, task_id, files) => {
  const formData = new FormData();
  // formData.append('user_google_id', userGoogleId);
  formData.append('task_id', task_id || "");
  files.forEach(file => {
    formData.append('files', file);
  });

  return fetchWithHandling(`${API_URL}/file/upload`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`
    },
    body: formData,
  });
};

// File upload function from google drive
export const uploadFilesFromDrive = async (accessToken, task_id, google_drive_file_ids) => {
  return fetchWithHandling(`${API_URL}/file/fromGoogleDrive/upload`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`
    },
    body: JSON.stringify({
      google_drive_file_ids,
      task_id: task_id || ""
    }),
  });
};

// File upload function from google drive link (URL-based)
export const uploadFilesFromDriveLink = async (accessToken, task_id, google_drive_urls) => {
  return fetchWithHandling(`${API_URL}/file/from-google-drive-link/upload`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`
    },
    body: JSON.stringify({
      google_drive_urls,
      task_id: task_id || ""
    }),
  });
};

// List files function
export const listFiles = async (accessToken, task_id) => {
  // const response = await fetch(`${API_URL}/file/files?user_google_id=${userGoogleId}&task_id=${task_id ? task_id : ""}`);
  return fetchWithHandling(`${API_URL}/file/files?task_id=${task_id ? task_id : ""}`, { headers: { 'Authorization': `Bearer ${accessToken}` } });

};

// File download function
export const downloadFile = async (fileId, accessToken) => {
  return fetchWithHandling(`${API_URL}/file/download/${fileId}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${accessToken}`
    }
  });
};

export const deleteFile = async (fileId, accessToken) => {
  return fetchWithHandling(`${API_URL}/file/delete/${fileId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${accessToken}`
    }
  });
};

// --- TEMPLATE API FUNCTIONS ---
export const listTemplates = async (accessToken) => {
  return fetchWithHandling(`${API_URL}/template/list`, {
    headers: { 'Authorization': `Bearer ${accessToken}` }
  });
};

// NEW API FUNCTION
export const getTemplateDetails = async (accessToken, templateId) => {
  return fetchWithHandling(`${API_URL}/template/${templateId}`, {
    headers: { 'Authorization': `Bearer ${accessToken}` }
  });
};

export const uploadTemplate = async (accessToken, templateName, templateUseCase, file, instructions) => {
  const formData = new FormData();
  formData.append('template_name', templateName);
  formData.append('template_use_case', templateUseCase);
  formData.append('file', file);
  formData.append('instructions', instructions);

  return fetchWithHandling(`${API_URL}/template/upload`, {
    method: 'POST',
    headers: { 'Authorization': `Bearer ${accessToken}` },
    body: formData,
  });
};

// +++ NEW API FUNCTION +++
export const updateTemplate = async (accessToken, templateId, updateData) => {
  return fetchWithHandling(`${API_URL}/template/update/${templateId}`, {
    method: 'PATCH',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(updateData),
  });
};

// +++ NEW API FUNCTION +++
export const toggleTemplateStatus = async (accessToken, templateId, isActive) => {
  return fetchWithHandling(`${API_URL}/template/toggle_status/${templateId}`, {
    method: 'PATCH',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ is_active: isActive }),
  });
};

export const deleteTemplate = async (accessToken, templateId) => {
  return fetchWithHandling(`${API_URL}/template/delete/${templateId}`, {
    method: 'DELETE',
    headers: { 'Authorization': `Bearer ${accessToken}` }
  });
};

export const downloadTemplate = async (accessToken, templateId) => {
  return fetchWithHandling(`${API_URL}/template/download/${templateId}`, {
    headers: { 'Authorization': `Bearer ${accessToken}` }
  });
};

// File upload function
export const uploadFilesDataSource = async (accessToken, files) => {
  const formData = new FormData();
  files.forEach(file => {
    formData.append('files', file);
  });

  return fetchWithHandling(`${API_URL}/dataSource/upload`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`
    },
    body: formData,
  });
};

// File upload function from google drive
export const uploadFilesFromDriveDataSource = async (accessToken, google_drive_file_ids) => {
  return fetchWithHandling(`${API_URL}/dataSource/fromGoogleDrive/upload`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`
    },
    body: JSON.stringify({
      google_drive_file_ids,
    }),
  });
};

// List files function
export const listFilesDataSource = async (accessToken) => {
  return fetchWithHandling(`${API_URL}/dataSource/files`, {
    headers: {
      'Authorization': `Bearer ${accessToken}`
    }
  });
};

// File download function
export const downloadFileDataSource = async (fileId, accessToken) => {
  return fetchWithHandling(`${API_URL}/dataSource/download/${fileId}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${accessToken}`
    },
  });
}

export const deleteFileDataSource = async (fileId, accessToken) => {
  return fetchWithHandling(`${API_URL}/dataSource/delete/${fileId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${accessToken}`
    },
  });
};

// Update task data sources function
export const updateTaskDataSources = async (taskId, accessToken, selectedFileIds) => {
  return fetchWithHandling(`${API_URL}/updateTaskDataSources`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`

    },
    body: JSON.stringify({
      task_id: taskId,
      selected_file_ids: selectedFileIds,
    }),
  });
};

// Get task data sources function
export const getTaskDataSources = async (taskId, accessToken) => {
  return fetchWithHandling(`${API_URL}/getTaskDataSources?task_id=${taskId}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${accessToken}`
    },
  });
};

// Get task data sources function
export const getDataSourceStatus = async (file_id, accessToken) => {
  return fetchWithHandling(`${API_URL}/dataSource/get_parse_status?file_id=${file_id}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${accessToken}`
    },
  });
};

export const getUserInfo = async (accessToken) => {
  return fetchWithHandling(`${API_URL}/api/user`, {
    headers: {
      'Authorization': `Bearer ${accessToken}`
    },
  });
}

// Delete task function
export const deleteTask = async (taskId, accessToken) => {
  return fetchWithHandling(`${API_URL}/deleteTask/${taskId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${accessToken}`
    },
  });
};

// Delete Bot
export const deleteBot = async (botId, accessToken) => {
  return fetchWithHandling(`${API_URL}/bot/${botId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${accessToken}`
    },
  });
};

export const initializeStreamChat = async (requestData, accessToken) => {
  return fetchWithHandling(`${API_URL}/initialize_stream_chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`
    },
    body: JSON.stringify(requestData),
  });
};

export const streamChat = (streamId, accessToken) => {
  const eventSource = new SSE(`${API_URL}/stream_chat/${streamId}?token=${encodeURIComponent(accessToken)}`, {
    headers: {
      'Authorization': `Bearer ${accessToken}`
    }
  });
  return eventSource;
};

export const completeStreamChat = (streamId, accessToken) => {
  const eventSource = new SSE(`${API_URL}/stream-completion-listener/${streamId}?token=${encodeURIComponent(accessToken)}`, {
    headers: {
      'Authorization': `Bearer ${accessToken}`
    }
  });
  return eventSource;
};

export const stopStreamChat = (streamId, accessToken) => {
  return fetchWithHandling(`${API_URL}/stream_chat/${streamId}/stop`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`
    },
  });
};

// Function to create a task bot
export const createTempBot = async (data, accessToken) => {
  return fetchWithHandling(`${API_URL}/createTempBot`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`
    },
    body: JSON.stringify(data),
  });
};

// Function to create a task bot
export const createTaskBot = async (data, accessToken) => {
  return fetchWithHandling(`${API_URL}/createTaskBot`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',

      'Authorization': `Bearer ${accessToken}`

    },
    body: JSON.stringify(data),
  });
};

// Function to create bot steps
export const createBotSteps = async (data, accessToken) => {
  return fetchWithHandling(`${API_URL}/createBotSteps`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',

      'Authorization': `Bearer ${accessToken}`

    },
    body: JSON.stringify(data),
  });
};

/**
 * Updates the status of a specific bot (e.g., enables/disables it).
 * @param {string} botId - The ID of the bot to update.
 * @param {object} payload - The data for the update, e.g., { disabled: true }.
 * @param {string} accessToken - The user's authorization token.
 * @returns {Promise<object>}
 */
export const updateBotStatus = async (botId, payload, accessToken) => {
  return fetchWithHandling(`${API_URL}/bots/${botId}/status`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
    body: JSON.stringify(payload),
  });
};

// Function to initialize a step stream
export const initializeStepStream = async (data, accessToken) => {

  return fetchWithHandling(`${API_URL}/initialize_step_stream`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',

      'Authorization': `Bearer ${accessToken}`

    },
    body: JSON.stringify(data),
  });
};

// Function to stream step chat
export const streamStepChat = (streamId, accessToken) => {
  try {
    const eventSource = new SSE(`${API_URL}/stream_step_chat/${streamId}?token=${encodeURIComponent(accessToken)}`, {
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    });
    console.log("Event source:", eventSource)
    return eventSource;
  } catch (error) {
    console.error('Error streaming step chat:', error);
    throw error;
  }
};

export const fetchBots = async (accessToken) => {
  return fetchWithHandling(`${API_URL}/bots`, {
    headers: {
      'Authorization': `Bearer ${accessToken}`
    },
  });

};

// src/services/api.js
export const fetchBotSteps = async (botId, accessToken) => {
  return fetchWithHandling(`${API_URL}/steps?bot_id=${botId}`, {
    headers: {
      'Authorization': `Bearer ${accessToken}`
    },
  });
};

export const updateBotStepPrompt = async (botId, stepId, accessToken, updatedPrompt) => {
  return fetchWithHandling(`${API_URL}/steps/update`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',

      'Authorization': `Bearer ${accessToken}`

    },
    body: JSON.stringify({
      bot_id: botId,
      step_id: stepId,
      // user_google_id: userGoogleId,
      step_prompt: updatedPrompt,
    }),
  });
};

export const checkTriggerPhrase = async (accessToken, triggerPhrase) => {

  return fetchWithHandling(`${API_URL}/check_trigger_phrase`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',

      'Authorization': `Bearer ${accessToken}`

    },
    body: JSON.stringify({ triggerPhrase })
  });
}

// Function to create a session - new field
export const createSession = async (data, accessToken) => {

  return fetchWithHandling(`${API_URL}/create_session`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',

      'Authorization': `Bearer ${accessToken}`

    },
    body: JSON.stringify(data),
  });
};

// Function to save step chat in a session
export const saveStepChat = async (bot_id, step_id, accessToken, session_id, step_chat) => {

  const data = { bot_id, step_id, session_id, step_chat }
  return fetchWithHandling(`${API_URL}/bot/session/save_step_chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',

      'Authorization': `Bearer ${accessToken}`

    },
    body: JSON.stringify(data),
  });
};

// Function to get step chats by session, step, user, and bot
export const fetchStepChats = async (botId, stepId, accessToken, sessionId) => {
  // Build the URL with query parameters
  return fetchWithHandling(`${API_URL}/bot/session/get_step_chats?bot_id=${botId}&step_id=${stepId}&session_id=${sessionId}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${accessToken}`
    },
  });
};

// Function to get sessions by bot and user
export const getSessionsByBotAndUser = async (botId, accessToken) => {

  return fetchWithHandling(`${API_URL}/sessions?bot_id=${botId}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${accessToken}`
    },
  });
};

export const getJiraResources = async (accessToken) => {

  return fetchWithHandling(`${API_URL}/auth/jira/resources`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${accessToken}`
    },
  });

}

export const getConfluenceResources = async (accessToken) => {

  return fetchWithHandling(`${API_URL}/auth/confluence/resources`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${accessToken}`
    },
  });

}

export const getAppResources = async (appname, accessToken) => {

  return fetchWithHandling(`${API_URL}/auth/${appname}/resources`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${accessToken}`
    },
  });

}

export const getAzureDevopsOrganizations = async (accessToken) => {

  return fetchWithHandling(`${API_URL}/auth/azure_devops/organizations`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${accessToken}`
    },
  });

}

// Function to get sessions by bot and user
export const getGmailMessageDetails = async (messageId, accessToken) => {

  return fetchWithHandling(`${API_URL}/email/gmail/${messageId}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${accessToken}`
    },
  });
};

export const getOutlookMessageDetails = async (messageId, accessToken) => {

  return fetchWithHandling(`${API_URL}/email/outlook/${messageId}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${accessToken}`
    },
  });
};

/**
 * Fetch a list of emails with optional filters, pagination, and sorting.
 * @param {Object} options - Options for filtering, pagination, and sorting.
 * @param {number} [options.priority] - Priority value of the email.
 * @param {boolean} [options.urgency] - Urgency flag.
 * @param {string} [options.senderClass] - Sender classification.
 * @param {string} [options.objectiveClass] - Objective classification.
 * @param {number} [options.page=1] - Page number (starting from 1).
 * @param {number} [options.page_size=10] - Number of emails per page.
 * @param {string} [options.sort_field] - Field to sort by.
 * @param {string} [options.sort_order="asc"] - Sort order: "asc" or "desc".
 * @param {string} token - JWT token for authentication.
 * @returns {Promise<Array>} List of email objects.
 */
export const getOutlookEmailsFromDB = async (
  {
    priority,
    urgency,
    senderClass,
    objectiveClass,
    isRead,
    Inbox,
    SentItems,
    page = 1,
    page_size = 10,
    sort_field,
    sort_order = "desc",
  } = {},
  token
) => {

  // Build query parameters
  const params = new URLSearchParams();
  if (priority !== undefined) params.append("priority", priority);
  if (urgency !== undefined) params.append("urgency", urgency);
  if (senderClass !== undefined) params.append("senderClass", senderClass);
  if (isRead !== undefined) params.append("isRead", isRead);
  if (SentItems !== undefined) params.append("folderName", "Sent Items");
  if (Inbox !== undefined) params.append("folderName", "Inbox");
  if (objectiveClass !== undefined)
    params.append("objectiveClass", objectiveClass);
  params.append("page", page);
  params.append("page_size", page_size);
  if (sort_field) params.append("sort_field", sort_field);
  if (sort_order) params.append("sort_order", sort_order);

  return fetchWithHandling(`${API_URL}/outlook_mails/emails?${params.toString()}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      // Include the JWT token in the Authorization header if required
      Authorization: `Bearer ${token}`,
    },
  });

}

export const getGmailEmailsFromDB = async (
  {
    priority,
    urgency,
    senderClass,
    objectiveClass,
    isRead,
    Inbox,
    SentItems,
    page = 1,
    page_size = 10,
    sort_field,
    sort_order = "desc",
  } = {},
  token
) => {

  // Build query parameters
  const params = new URLSearchParams();
  if (priority !== undefined) params.append("priority", priority);
  if (urgency !== undefined) params.append("urgency", urgency);
  if (senderClass !== undefined) params.append("senderClass", senderClass);
  if (isRead !== undefined) params.append("isRead", isRead);
  if (SentItems !== undefined) params.append("folderName", "Sent Items");
  if (Inbox !== undefined) params.append("folderName", "Inbox");
  if (objectiveClass !== undefined)
    params.append("objectiveClass", objectiveClass);
  params.append("page", page);
  params.append("page_size", page_size);
  if (sort_field) params.append("sort_field", sort_field);
  if (sort_order) params.append("sort_order", sort_order);

  return fetchWithHandling(`${API_URL}/gmail_emails/emails?${params.toString()}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      // Include the JWT token in the Authorization header if required
      Authorization: `Bearer ${token}`,
    },
  });

}

/**
 * Fetch details of a single email by its message_id.
 * @param {string} message_id - The message_id of the email.
 * @param {string} token - JWT token for authentication.
 * @returns {Promise<Object>} The email details object.
 */
export const getOutlookEmailDetailsFromDB = async (message_id, accessToken) => {

  return fetchWithHandling(
    `${API_URL}/outlook_mails/emails/${encodeURIComponent(message_id)}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );

}

export const getGmailEmailDetailsFromDB = async (message_id, accessToken) => {

  return fetchWithHandling(
    `${API_URL}/gmail_emails/emails/${encodeURIComponent(message_id)}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );

}

export const deleteOutlookMailsFromDB = async (token) => {

  return fetchWithHandling(`${API_URL}/outlook_mails/delete`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

}

export const deleteGmailMailsFromDB = async (token) => {
  return fetchWithHandling(`${API_URL}/gmail_emails/delete`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

}

export const getJiraIssueDetails = async (issue_key, accessToken) => {
  return fetchWithHandling(`${API_URL}/jira_issues_and_comments/issue/${issue_key}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${accessToken}`
    },
  });
};

// Function to update the jira issue
export const updateJiraIssue = async (issue_key, updateData, accessToken) => {
  return fetchWithHandling(`${API_URL}/jira_issue_update/issue/${issue_key}`, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(updateData),
  });
};

export const fetchJiraUsers = async (query, accessToken) => {
  const url = `${API_URL}/search/jiraUsers?start=0&limit=50&query=${encodeURIComponent(query)}`;

  const options = {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
  };
  return fetchWithHandling(url, options);
};

// File upload function
export const uploadImages = async (accessToken, task_id, files) => {
  const formData = new FormData();
  formData.append('task_id', task_id || "");
  files.forEach(file => {
    formData.append('files', file);
  });

  return fetchWithHandling(`${API_URL}/image/upload`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`
    },
    body: formData,
  });
};

// List files function
export const listImages = async (accessToken, task_id) => {
  return fetchWithHandling(`${API_URL}/image/images?task_id=${task_id ? task_id : ""}`, { headers: { 'Authorization': `Bearer ${accessToken}` } });
};

export const deleteImage = async (fileId, accessToken) => {
  return fetchWithHandling(`${API_URL}/image/delete/${fileId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${accessToken}`
    }
  });
};

export const fetchSessionSteps = async (sessionId, accessToken) => {
  const url = `${API_URL}/session/${sessionId}/steps`;

  const options = {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
  };
  return fetchWithHandling(url, options);
};

export const fetchBotsHistory = async (accessToken) => {
  const url = `${API_URL}/valid_session_bots`;

  const options = {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
  };
  return fetchWithHandling(url, options);
};

export const fetchExecutionBotSessions = async (accessToken) => {
  const url = `${API_URL}/bots/executions`;

  const options = {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
  };
  return fetchWithHandling(url, options);
};

export const executeBot = async (botId, accessToken) => {
  const url = `${API_URL}/bot/execute/${botId}`;

  const options = {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
  };
  return fetchWithHandling(url, options);
};

// Add a comment to a Jira issue
export const addJiraComment = async (issue_key, commentData, accessToken) => {
  return fetchWithHandling(`${API_URL}/jira_issue_update/issue/${issue_key}/comment`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(commentData),
  });
};

// Update a comment on a Jira issue
export const updateJiraComment = async (issue_key, comment_id, commentBody, accessToken) => {

  return fetchWithHandling(`${API_URL}/jira_issue_update/issue/${issue_key}/comment/${comment_id}`, {
    method: 'PUT',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ body: commentBody }),
  });
};

// Delete a comment from a Jira issue
export const deleteJiraComment = async (issue_key, comment_id, accessToken) => {

  return fetchWithHandling(`${API_URL}/jira_issue_update/issue/${issue_key}/comment/${comment_id}`, {
    method: 'DELETE',
    headers: { 'Authorization': `Bearer ${accessToken}` },
  });
};

export const fetchJiraIssues = async (projectKey = 'KAN', accessToken) => {
  const url = `${API_URL}/jira_issue_update/fetch_issues?projectKey=${projectKey}`;

  return fetchWithHandling(url, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
  }
  );
};

// forward and an Email to a list of recepients
export const forwardOutlookEmail = async (accessToken, forwardData) => {
  return fetchWithHandling(`${API_URL}/outlook_mails/forward`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(forwardData),
  });
};

export const forwardGmailEmail = async (accessToken, forwardData) => {
  return fetchWithHandling(`${API_URL}/gmail_emails/forward`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(forwardData),
  });
};

//get outlook email conversation thread
export const getOutlookEmailThread = async (accessToken, conversationId) => {
  const url = new URL(`${API_URL}/outlook_mails/conversation`);
  url.searchParams.append("conversationId", conversationId);

  return fetchWithHandling(url.toString(), {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
  });
};

export const getGmailEmailThread = async (accessToken, conversationId) => {
  const url = new URL(`${API_URL}/gmail_emails/conversation`);

  // Append required query parameters
  url.searchParams.append("conversationId", conversationId);

  return fetchWithHandling(url.toString(), {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
  });
};

// compose an Email
export const draftEmail = async (accessToken, body) => {
  const url = `${API_URL}/outlook_mails/draft_email`;
  return fetchWithHandling(url, {
    method: "POST",
    headers: {
      "Authorization": `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
};

// send an email
export const sendOutlookMail = async (accessToken, formData) => {
  const url = `${API_URL}/outlook_mails/send`;
  return fetchWithHandling(url, {
    method: 'POST',
    body: formData,
    headers: {
      'Authorization': `Bearer ${accessToken}`,
    },
  });
};

// Function to get the list of emails after searching
export const searchEmails = async (accessToken, searchQuery = '', filterConditions = '', page = 1, pageSize = 10, isOutlook = true) => {
  // Determine the API endpoint based on email type
  const endpoint = isOutlook ? "/outlook_mails/search_emails" : "/gmail_emails/search_emails";
  const url = new URL(`${API_URL}${endpoint}`);

  // Append required query parameters
  if (searchQuery) {
    url.searchParams.append("search_query", searchQuery);
  }
  if (isOutlook && filterConditions) {
    url.searchParams.append("filter_conditions", filterConditions);
  }
  url.searchParams.append("page", page);
  url.searchParams.append("page_size", pageSize);

  return fetchWithHandling(url.toString(), {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
  });
};

export const getEmailSuggestions = async (accessToken, query, limit = 10) => {
  const url = `${API_URL}/email/suggestions?query=${encodeURIComponent(query)}&limit=${limit}`;

  return fetchWithHandling(url, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
  });
};

/**
 * Fetches agent descriptions and agent tree.
 * @param {string} accessToken - The user's JWT or session token.
 * @returns {Promise<object>} - A promise that resolves with agent_descriptions and agent_tree.
 */
export const fetchAgentInfo = async (accessToken) => {
  return fetchWithHandling(`${API_URL}/agents/info`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
  });
};

/**
 * Updates the agent sequence for a specific task on the backend.
 * @param {string} accessToken - The user's JWT or session token.
 * @param {string} taskId - The ID of the task to update.
 * @param {string[]} sequence - The new agent sequence array.
 * @returns {Promise<object>} - A promise that resolves with the server's response.
 */
export const updateAgentSequence = async (accessToken, taskId, sequence) => {
  return fetchWithHandling(`${API_URL}/agents/update_agent_sequence`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
    body: JSON.stringify({
      task_id: taskId,
      agent_sequence: sequence,
    }),
  });
};

/**
 * Submits user feedback to Linear via the backend.
 * @param {FormData} formData - FormData containing type, description, subject, and attachments
 * @returns {Promise<object>} - A promise that resolves with the feedback submission result.
 */
export const submitFeedback = async (formData) => {
  const token = localStorage.getItem('projectToken');
  const headers = {};

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_URL}/web/api/feedback`, {
    method: 'POST',
    body: formData,
    headers: headers,
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || errorData.message || `Error: ${response.status}`);
  }

  return response.json();
};


/**
 * Fetches the Accounts Receivable dashboard data including metrics,
 * aging analysis, and customer list.
 * @param {string} accessToken - The user's JWT or session token.
 * @param {string} startDate - Optional start date in YYYY-MM-DD format.
 * @param {string} endDate - Optional end date in YYYY-MM-DD format.
 * @param {string} filterType - Optional filter type: 'created', 'due', or 'both'.
 * @returns {Promise<object>} - The dashboard data object.
 */
export const fetchArDashboardData = async (accessToken, startDate = null, endDate = null, filterType = "created") => {
  const params = new URLSearchParams();
  if (startDate) params.append("start_date", startDate);
  if (endDate) params.append("end_date", endDate);
  if (filterType) params.append("filter_type", filterType);

  const queryString = params.toString() ? `?${params.toString()}` : "";

  return fetchWithHandling(`${API_URL}/app/quickbooks/dashboard${queryString}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
  });
};